import { cva } from 'class-variance-authority'
import * as React from 'react'
import { Progress as ProgressPrimitive } from 'radix-ui'

import { cn } from '@ui/lib/utils'

export type ProgressTone = 'neutral' | 'success' | 'warning' | 'error' | 'info'
export type ProgressSize = 'sm' | 'md' | 'lg'

const progressVariants = cva('relative w-full overflow-hidden rounded-full bg-secondary', {
  variants: {
    size: {
      sm: 'h-1.5',
      md: 'h-2.5',
      lg: 'h-4',
    },
  },
  defaultVariants: { size: 'md' },
})

const indicatorVariants = cva('h-full w-full origin-left transition-transform', {
  variants: {
    tone: {
      neutral: 'bg-primary',
      success: 'bg-success',
      warning: 'bg-warning',
      error: 'bg-destructive',
      info: 'bg-info',
    },
  },
  defaultVariants: { tone: 'neutral' },
})

export interface ProgressProps
  extends Omit<React.ComponentProps<typeof ProgressPrimitive.Root>, 'value' | 'max'> {
  value: number
  max?: number
  tone?: ProgressTone
  size?: ProgressSize
}

export function Progress({
  value,
  max = 100,
  tone = 'neutral',
  size = 'md',
  className,
  ...props
}: ProgressProps) {
  const safeMax = Number.isFinite(max) && max > 0 ? max : 100
  const finiteValue = Number.isFinite(value) ? value : 0
  const safeValue = Math.min(safeMax, Math.max(0, finiteValue))
  const percentage = (safeValue / safeMax) * 100

  return (
    <ProgressPrimitive.Root
      data-slot="progress"
      value={safeValue}
      max={safeMax}
      className={cn(progressVariants({ size }), className)}
      {...props}
    >
      <ProgressPrimitive.Indicator
        data-slot="progress-indicator"
        className={indicatorVariants({ tone })}
        style={{ transform: `translateX(-${100 - percentage}%)` }}
      />
    </ProgressPrimitive.Root>
  )
}
